import { GridFilterModel } from '@mui/x-data-grid-premium';
import { JSONValue } from '../types';
import { CONFIG_SCHEMA } from '../services/config';
import { postRequest } from './postApi';

type RoleRecord = {
  id: number;
  role_name: string;
};

export type RoleAccessEntry = {
  roleName: string;
  hasRole: boolean;
  id: number | null;
};

export type RoleAccessResult = {
  hasAnyRole: boolean;
  roleAccessList: RoleAccessEntry[];
};

export type RoleAccessParams = {
  roleIds: number[];
  roleNames: string[];
  screenGridId: number;
  schema?: string;
  table?: string;
};

function buildRoleFilterModel(roleNames: string[]): GridFilterModel {
  return {
    items: [
      {
        field: 'role_name',
        operator: 'isAnyOf',
        value: roleNames,
      },
    ],
    logicOperator: 'and' as GridFilterModel['logicOperator'],
    quickFilterValues: [],
  };
}

async function fetchRoleRecords(
  roleNames: string[],
  screenGridId: number,
  schema: string = CONFIG_SCHEMA || 'mbdcm',
  table = 'mbd_roles',
): Promise<RoleRecord[]> {
  const filterModel = buildRoleFilterModel(roleNames);
  const body = {
    columns: ['id', 'role_name'],
    screenGridId,
    schema,
    table,
    filterModel,
    orderCol: 'id',
    order: 'asc',
  };

  const { data, errors } = await postRequest('/api/records', body as unknown as JSONValue);
  if (errors) {
    console.error('Failed to fetch role records', errors);
    return [];
  }

  return data?.[`${schema}_${table}`] || [];
}

function checkRoleIdsAgainstRecords(
  roleIds: number[],
  roleRecords: RoleRecord[],
  roleNames: string[],
): RoleAccessResult {
  const roleAccessList: RoleAccessEntry[] = [];
  const normalizedRoleIds = roleIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));

  let hasAnyRole = false;
  roleNames.forEach((roleName) => {
    const record = roleRecords.find((role) => role.role_name === roleName);
    const hasRole = record ? normalizedRoleIds.includes(record.id) : false;
    if (hasRole) {
      hasAnyRole = true;
    }
    roleAccessList.push({
      roleName,
      hasRole,
      id: record?.id ?? null,
    });
  });

  return { hasAnyRole, roleAccessList };
}

/**
 * Fetches role records for the provided role names and checks whether any
 * of the supplied role IDs match those records.
 *
 * @param params - Role access query details.
 * @param params.roleIds - Role IDs assigned to the current user.
 * @param params.roleNames - Role names to check (case-insensitive).
 * @param params.screenGridId - Screen grid ID used for access validation.
 * @param params.schema - Optional config schema override.
 * @param params.table - Optional roles table override.
 * @returns Access summary including per-role presence and overall match flag.
 */
export async function getRoleAccessStatus({
  roleIds,
  roleNames,
  screenGridId,
  schema,
  table,
}: RoleAccessParams): Promise<RoleAccessResult> {
  const records = await fetchRoleRecords(roleNames, screenGridId, schema, table);
  return checkRoleIdsAgainstRecords(roleIds, records, roleNames);
}
